function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/* eslint-disable camelcase */
import { toHex } from '@aws-sdk/util-hex-encoding';
import fetch from 'unfetch';
import { v1 as uuid } from 'uuid';
import { Auth } from './aws-auth';
import { Signer } from './aws-signer';
import { canonical_request, formatDate, generateCredentialScope } from './canonical-request';
import { CryptoHash } from './util/crypto-hash';
import { convertCamelToPascal, convertPascalToCamel } from './util/name-conventions';
import { dateUtil, convertToUrlSearchString } from './util/utils';
async function hash(src) {
  const sh256 = new CryptoHash();
  sh256.update(src);
  return toHex(await sh256.digest());
}
async function buildSignStr(algorithm, canonicalRequest, dt_str, scope) {
  const hashR = await hash(canonicalRequest);
  return [algorithm, dt_str, scope, hashR].join('\n');
}
const DEFAULT_ALGORITHM = 'AWS4-HMAC-SHA256';
export class PinPointClient {
  constructor(authConfig, pinpointConfig) {
    _defineProperty(this, "signer", void 0);
    _defineProperty(this, "pinpointConfig", void 0);
    _defineProperty(this, "auth", void 0);
    _defineProperty(this, "commonRequestField", void 0);
    _defineProperty(this, "credential", void 0);
    this.auth = new Auth(authConfig);
    this.pinpointConfig = pinpointConfig;
    this.commonRequestField = {
      protocol: 'https',
      hostname: `pinpoint.${this.pinpointConfig.region}.amazonaws.com`,
      port: 443,
      headers: {
        'content-type': 'application/json'
      }
    };
  }
  async init() {
    const newCredential = await this.auth.getCredential();
    if (!newCredential || newCredential === this.credential) {
      return;
    }
    this.credential = newCredential;
    this.signer = new Signer(this.credential, this.pinpointConfig.region, 'Pinpoint');
  }
  async putEvent(eventsRequest) {
    const request = {
      ...this.commonRequestField,
      method: 'POST',
      path: `/v1/apps/${this.pinpointConfig.appId}/events`,
      body: JSON.stringify(convertCamelToPascal(eventsRequest))
    };
    return (await this.sendRequest(request)).results;
  }
  async putEventBySendBeacon(eventsRequest) {
    var _this$signer;
    const credential = await this.auth.getCredential();
    if (!credential) {
      return;
    }
    const now = formatDate(dateUtil.getDateWithClockOffset().getTime());
    const credentialScope = generateCredentialScope(now.shortDate, this.pinpointConfig.region, 'mobiletargeting');
    const queryParams = {
      'X-Amz-Algorithm': DEFAULT_ALGORITHM,
      'X-Amz-Credential': [credential.accessKeyId, credentialScope].join('/'),
      'X-Amz-Date': now.longDate,
      'X-Amz-Security-Token': `${credential.sessionToken}`,
      'X-Amz-SignedHeaders': 'host'
    };
    const searchString = convertToUrlSearchString(queryParams, true);
    const url = new URL(`${this.commonRequestField.protocol}://${this.commonRequestField.hostname}/v1/apps/${this.pinpointConfig.appId}/events${searchString}`);
    const signedHeaders = {
      host: url.host
    };
    const data = await hash(JSON.stringify(convertCamelToPascal(eventsRequest)));
    const canonicalRequest = canonical_request({
      method: 'POST',
      url,
      headers: signedHeaders,
      data
    });
    const signature = await ((_this$signer = this.signer) === null || _this$signer === void 0 ? void 0 : _this$signer.signStr(await buildSignStr(DEFAULT_ALGORITHM, canonicalRequest, now.longDate, credentialScope)));
    const additionalQueryParams = {
      'X-Amz-Signature': signature
    };
    const result = `${url}&${convertToUrlSearchString(additionalQueryParams)}`;
    navigator.sendBeacon(result, JSON.stringify(convertCamelToPascal(eventsRequest)));
  }
  async updateEndpoint(applicationId, endpointId, endpoint) {
    const request = {
      ...this.commonRequestField,
      method: 'PUT',
      path: `/v1/apps/${applicationId}/endpoints/${endpointId}`,
      body: JSON.stringify(convertCamelToPascal({
        requestId: uuid(),
        effectiveDate: new Date().toISOString(),
        ...endpoint
      }))
    };
    return this.sendRequest(request);
  }
  async sendRequest(request, retry = true) {
    var _this$signer2;
    const signedRequest = await ((_this$signer2 = this.signer) === null || _this$signer2 === void 0 ? void 0 : _this$signer2.sign(request));
    signedRequest === null || signedRequest === void 0 ? true : delete signedRequest.headers.host;
    const result = await fetch(`${signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.protocol}://${signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.hostname}${signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.path}`, {
      method: signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.method,
      headers: signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.headers,
      body: signedRequest === null || signedRequest === void 0 ? void 0 : signedRequest.body
    });
    if (result.ok) {
      return convertPascalToCamel(await result.json());
    }
    const error = await result.json();
    if (retry && dateUtil.isClockSkewError(error.message)) {
      if (dateUtil.clockOffset !== 0) {
        dateUtil.resetClockOffset();
      } else {
        dateUtil.setClockOffsetByErrorMsg(error.message);
      }
      return this.sendRequest(request, false);
    }
    throw error.message;
  }
}